import { useState, useEffect } from "react";
import { Button, Box, InputAdornment, TextField } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import AddIcon from "@mui/icons-material/Add";
import CourseList from "@nualang/nualang-ui-components/dist/Lists/Courses";
import CoursesNotFound from "@nualang/nualang-ui-components/dist/Misc/NotFound/Courses";
import LanguageSelector from "@nualang/nualang-ui-components/dist/Forms/LanguageSelector/LanguageSelector";
import HideOnScroll from "@nualang/nualang-ui-components/dist/Misc/HideOnScroll";
import SortBySelector from "@nualang/nualang-ui-components/dist/Forms/SortBySelector/SortBySelector";
import SearchIcon from "@mui/icons-material/Search";
import useDebounce from "@nualang/nualang-ui-components/dist/hooks/useDebounce";
import QuickCreateCourseDialog from "@nualang/nualang-ui-components/dist/Dialogs/QuickCreateCourseDialog/QuickCreateCourseDialog";

const useStyles = makeStyles()((theme, { disablePadding }) => ({
  root: {
    height: "100%",
    padding: disablePadding ? 0 : theme.spacing(1, 2),
    [theme.breakpoints.up("sm")]: {
      padding: disablePadding ? 0 : theme.spacing(2, 3),
    },
  },
  box: {
    padding: theme.spacing(1, 2),
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(2, 3),
    },
  },
  autocomplete: {
    flexGrow: 1,
    marginRight: theme.spacing(1),
  },
  actions: {
    marginBottom: theme.spacing(1),
  },
  divider: {
    marginBottom: theme.spacing(1),
  },
}));

function SearchFilters({
  t,
  classes,
  languageOptions,
  forLanguageOptions,
  handleFilterChange,
  sortByOptions,
  learnLang,
  search,
  setSearch,
  forLang,
  sort,
  difficulty,
  difficultyOptions,
  showCreateCourseButton,
  handleQuickCreateCourse,
  verificationStatus,
  userImage,
  fileSizeLimit,
  openSnackbar,
}) {
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText, 500);
  const [isCreateCourseOpen, setIsCreateCourseOpen] = useState(false);
  useEffect(() => {
    if (setSearch && searchText !== search) {
      setSearch(searchText);
      if (window.scrollY > 100) {
        window.scrollTo(0, 0);
      }
    }
  }, [debouncedSearch]);
  return (
    <HideOnScroll threshold={300}>
      <Box
        className={classes.box}
        sx={[{
          display: 'flex',
          mb: 2
        }, (theme) => ({
          position: "sticky",
          top: 0,
          zIndex: 1200,
          backgroundColor: theme.palette.background.default,
          borderBottom: `1px solid ${theme.palette.divider}`,
          marginTop: theme.spacing(0.5),
        })]}>
        <Box
          className={classes.actions}
          sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", width: "100%" }}
        >
          <TextField
            id='search'
            name='search'
            data-cy='search'
            label={t("search")}
            placeholder={t("app_search_placeholder") || "Search courses"}
            value={searchText}
            onChange={(ev) => setSearchText(ev.target.value)}
            size="small"
            variant="outlined"
            sx={{ flex: "1 1 200px", maxWidth: 300 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          <LanguageSelector
            id={"learnLang"}
            name={"learnLang"}
            label={t("learn")}
            size="small"
            variant={"outlined"}
            sx={{ minWidth: 150 }}
            t={t}
            languages={languageOptions}
            value={learnLang}
            onChange={handleFilterChange}
          />
          <LanguageSelector
            id={"forLang"}
            name={"forLang"}
            label={t("speakers_of")}
            size="small"
            variant={"outlined"}
            sx={{ minWidth: 150 }}
            t={t}
            languages={forLanguageOptions}
            value={forLang}
            onChange={handleFilterChange}
          />
          <TextField
            select
            id='difficulty'
            name='difficulty'
            data-cy='difficulty'
            label={t("difficulty")}
            value={difficulty}
            onChange={handleFilterChange}
            size="small"
            variant={"outlined"}
            sx={{ minWidth: 130 }}
          >
            {difficultyOptions}
          </TextField>
          <SortBySelector
            id={"sortBy"}
            name={"sort"}
            label={t("sort")}
            size="small"
            variant={"outlined"}
            sx={{ minWidth: 150 }}
            t={t}
            value={sort}
            onChange={handleFilterChange}
            sortByOptions={sortByOptions}
            disabled={
              (!learnLang || learnLang === "all") &&
              (!forLang || forLang === "all")
            }
          />
          {showCreateCourseButton && (
            <Box sx={{ ml: "auto" }}>
              <Button
                onClick={() => setIsCreateCourseOpen(true)}
                variant='contained'
                color='primary'
                endIcon={<AddIcon />}
                role='link'
              >
                {t("create_course")}
              </Button>
              <QuickCreateCourseDialog
                open={isCreateCourseOpen}
                handleClose={() => setIsCreateCourseOpen(false)}
                handleCreate={async (values) => {
                  await handleQuickCreateCourse(values);
                }}
                t={t}
                learnLang={learnLang}
                forLang={forLang}
                languages={languageOptions}
                forLanguages={forLanguageOptions}
                fileSizeLimit={fileSizeLimit}
                verificationStatus={verificationStatus}
                userImage={userImage}
                openSnackbar={openSnackbar}
              />
            </Box>
          )}
        </Box>
      </Box>
    </HideOnScroll>
  );
}
export default function SearchCourses({
  t = (text) => text,
  courses = [],
  selectedCourses = [],
  isLoading,
  placeholderImageUrl,
  handleSelectAllCoursesClick,
  handleClickCourse,
  selectable,
  learnLang,
  forLang,
  handleViewCourseTopic,
  handleFilterChange,
  languages,
  forLanguages,
  handleStartCourse,
  handleLeaveCourse,
  handleViewCourse,
  handleViewUserProfile,
  membersProps,
  getMemberDetails,
  memberPlaceholderImageUrl,
  disablePadding,
  getCourseSections,
  getCourseMember,
  getCourseMembers,
  memberId,
  username,
  sort,
  search,
  setSearch,
  handleDuplicateCourse,
  difficulty,
  difficultyOptions,
  showCreateCourseButton,
  handleQuickCreateCourse,
  verificationStatus,
  userImage,
  fileSizeLimit,
  openSnackbar,
}) {
  const { classes } = useStyles({ disablePadding });
  const languageOptions = {
    all: {},
    ...languages,
  };
  const forLanguageOptions = {
    all: {},
    ...forLanguages,
  };

  const sortByOptions = {
    newest: "newest",
    oldest: "oldest",
    popular: "popular",
  };

  return (
    <div>
      <SearchFilters
        t={t}
        classes={classes}
        languageOptions={languageOptions}
        forLanguageOptions={forLanguageOptions}
        handleFilterChange={handleFilterChange}
        sortByOptions={sortByOptions}
        learnLang={learnLang}
        search={search}
        setSearch={setSearch}
        sort={sort}
        forLang={forLang}
        difficulty={difficulty}
        difficultyOptions={difficultyOptions}
        showCreateCourseButton={showCreateCourseButton}
        handleQuickCreateCourse={handleQuickCreateCourse}
        verificationStatus={verificationStatus}
        userImage={userImage}
        fileSizeLimit={fileSizeLimit}
        openSnackbar={openSnackbar}
      />
      {isLoading || (courses !== undefined && courses.length > 0) ? (
        <div className={classes.root}>
          <CourseList
            t={t}
            courses={
              courses !== undefined && Array.isArray(courses) && courses.length
                ? courses
                : null
            }
            handleStartCourse={handleStartCourse}
            handleLeaveCourse={handleLeaveCourse}
            handleViewCourse={handleViewCourse}
            handleViewUserProfile={handleViewUserProfile}
            membersProps={membersProps}
            handleViewCourseTopic={handleViewCourseTopic}
            selectable={selectable}
            selectedCourses={selectedCourses}
            handleSelectAllCoursesClick={handleSelectAllCoursesClick}
            handleClickCourse={handleClickCourse}
            getMemberDetails={getMemberDetails}
            memberPlaceholderImageUrl={memberPlaceholderImageUrl}
            getCourseSections={getCourseSections}
            getCourseMember={getCourseMember}
            getCourseMembers={getCourseMembers}
            memberId={memberId}
            username={username}
            handleDuplicateCourse={handleDuplicateCourse}
          />
        </div>
      ) : (
        <div className={classes.root}>
          <CoursesNotFound
            t={t}
            placeholderImageUrl={placeholderImageUrl}
            title={t("courses_not_found_title")}
            description={t("courses_not_found_desc")}
            handleQuickCreateCourse={handleQuickCreateCourse}
            verificationStatus={verificationStatus}
            userImage={userImage}
            fileSizeLimit={fileSizeLimit}
          />
        </div>
      )}
    </div>
  );
}
